/**
 * Embed helpers for the rich text editor. The allowlist itself travels in the
 * session payload (features.richTextEmbedHosts) and is enforced again by the
 * host on save — everything here is editor UX: deciding when to offer
 * paste-to-embed and turning a page URL into its embeddable player URL.
 */

/**
 * Mirror of the host's matching rule: https only, exact host or subdomain.
 *
 * @param {string} url
 * @param {string[]} allowedHosts
 * @returns {boolean}
 */
export function embedHostAllowed(url, allowedHosts) {
    let host;
    try {
        const parsed = new URL(url);
        if (parsed.protocol !== 'https:') return false;
        host = parsed.hostname.toLowerCase();
    } catch {
        return false;
    }

    return (allowedHosts ?? []).some(allowed => host === allowed || host.endsWith(`.${allowed}`));
}

/**
 * Providers whose watch pages cannot be framed: a page URL of theirs has to be
 * recognised and rewritten, so a shape we do not recognise is not embeddable at
 * all. Every other allowlisted host hands out embeddable URLs already (players
 * like skiv or kaltura) and is framed unchanged.
 */
const PAGE_PROVIDERS = [
    'youtube.com',
    'youtube-nocookie.com',
    'youtu.be',
    'vimeo.com',
    'dailymotion.com',
    'soundcloud.com',
    'open.spotify.com',
];

/**
 * A URL copied out of running text drags the punctuation that closed the
 * sentence with it, and that punctuation lands inside the video id.
 */
const TRAILING_PUNCTUATION = /[.,;:!?)\]}>"'»]+$/;

/**
 * The mobile and music front ends serve the same watch pages as the desktop
 * one, under host names of their own.
 */
function providerHost(hostname) {
    return hostname.toLowerCase().replace(/^(?:www|m|music)\./, '');
}

/**
 * Where the reader asked the video to start, in seconds. Watch pages write it
 * as `t`, either as a plain number or in the 1h2m3s shorthand; the player only
 * understands `start`, and only in seconds.
 *
 * @param {URL} parsed
 * @returns {number|null}
 */
function startSeconds(parsed) {
    const raw = parsed.searchParams.get('t') ?? parsed.searchParams.get('start');
    const parts = (raw ?? '').match(/^(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s?)?$/);

    if (!raw || !parts) {
        return null;
    }

    const seconds = Number(parts[1] ?? 0) * 3600 + Number(parts[2] ?? 0) * 60 + Number(parts[3] ?? 0);

    return seconds > 0 ? seconds : null;
}

/**
 * The player URL for a YouTube video, carrying over the start time and the
 * playlist the watch page was playing it from.
 *
 * @param {string} path - Everything after /embed/, a video id or `videoseries`
 * @param {URL} parsed - The pasted page URL
 * @returns {string}
 */
function youTubePlayer(path, parsed) {
    const player = new URL(`https://www.youtube.com/embed/${path}`);
    const start = startSeconds(parsed);
    const list = parsed.searchParams.get('list');

    if (start) player.searchParams.set('start', String(start));
    if (list) player.searchParams.set('list', list);

    return player.toString();
}

/**
 * @param {URL} parsed
 * @returns {string|null}
 */
function youTubeSrcFor(parsed) {
    const { pathname } = parsed;

    // Already a player URL: nothing to rewrite, and rewriting would drop the
    // parameters whoever built it chose.
    if (/^\/embed\//.test(pathname)) {
        return parsed.toString();
    }

    const inPath = pathname.match(/^\/(?:shorts|live|v)\/([\w-]+)/);
    if (inPath) {
        return youTubePlayer(inPath[1], parsed);
    }

    const videoId = parsed.searchParams.get('v');
    if (videoId) {
        return youTubePlayer(videoId, parsed);
    }

    // A playlist with no video names its first one through `videoseries`.
    if (pathname === '/playlist' && parsed.searchParams.get('list')) {
        return youTubePlayer('videoseries', parsed);
    }

    return null;
}

/**
 * @param {URL} parsed
 * @returns {string|null}
 */
function vimeoSrcFor(parsed) {
    // An unlisted video is only playable with the hash its URL carries as a
    // second segment; the player takes it as `h`.
    const video = parsed.pathname.match(/^\/(?:channels\/[\w-]+\/|groups\/[\w-]+\/videos\/)?(\d+)(?:\/(\w+))?\/?$/);

    if (!video) {
        return null;
    }

    const player = new URL(`https://player.vimeo.com/video/${video[1]}`);

    if (video[2]) player.searchParams.set('h', video[2]);
    // The player reads its own start time off the fragment, unchanged.
    if (/^#t=/.test(parsed.hash)) player.hash = parsed.hash;

    return player.toString();
}

/**
 * The SoundCloud widget takes the track's own page URL as a parameter rather
 * than an id, so the address is carried over whole — minus the share and
 * campaign parameters a copied link drags along, which are tracking and would
 * otherwise be stored with the note. `secret_token` is the exception: a
 * private track is unplayable without it, the way an unlisted Vimeo video
 * needs its hash.
 *
 * @param {URL} parsed
 * @returns {string|null}
 */
function soundCloudSrcFor(parsed) {
    if (parsed.pathname === '/' || parsed.pathname === '') {
        return null;
    }

    const page = new URL(`https://soundcloud.com${parsed.pathname}`);
    const secret = parsed.searchParams.get('secret_token');

    if (secret) page.searchParams.set('secret_token', secret);

    const player = new URL('https://w.soundcloud.com/player/');
    player.searchParams.set('url', page.toString());

    return player.toString();
}

/**
 * Spotify embeds every kind of page it serves under the same /embed prefix.
 * The optional intl- segment is the localized front end of the same page.
 *
 * @param {URL} parsed
 * @returns {string|null}
 */
function spotifySrcFor(parsed) {
    if (/^\/embed\//.test(parsed.pathname)) {
        return parsed.toString();
    }

    const media = parsed.pathname.match(/^\/(?:intl-[\w-]+\/)?(track|album|playlist|episode|show|artist)\/(\w+)/);

    return media ? `https://open.spotify.com/embed/${media[1]}/${media[2]}` : null;
}

/**
 * Page URL → embeddable iframe src. Unknown-but-allowed hosts frame the URL
 * unchanged; a page URL from a provider whose shape is not recognised returns
 * null, which is how the caller knows not to offer an embed that would only
 * ever render a broken box.
 *
 * @param {string} url
 * @returns {string|null}
 */
export function embedSrcFor(url) {
    let parsed;
    try {
        parsed = new URL(url.replace(TRAILING_PUNCTUATION, ''));
    } catch {
        return url;
    }

    const host = providerHost(parsed.hostname);

    if (host === 'youtu.be') {
        const videoId = parsed.pathname.slice(1);

        return videoId ? youTubePlayer(videoId, parsed) : null;
    }

    if (host === 'youtube.com' || host === 'youtube-nocookie.com') {
        return youTubeSrcFor(parsed);
    }

    if (host === 'vimeo.com') {
        return vimeoSrcFor(parsed);
    }

    if (host === 'soundcloud.com') {
        return soundCloudSrcFor(parsed);
    }

    if (host === 'open.spotify.com') {
        return spotifySrcFor(parsed);
    }

    if (host === 'dailymotion.com') {
        const videoId = parsed.pathname.match(/^\/(?:embed\/)?video\/(\w+)/);

        return videoId ? `https://www.dailymotion.com/embed/video/${videoId[1]}` : null;
    }

    return PAGE_PROVIDERS.includes(host) ? null : url;
}

/**
 * A pasted clipboard counts as "a URL" only when it is exactly one https URL
 * and nothing else — anything longer is prose and pastes as text.
 *
 * @param {string} text
 * @returns {string|null}
 */
export function singlePastedUrl(text) {
    const candidate = (text ?? '').trim();

    if (!candidate || /\s/.test(candidate) || !candidate.startsWith('https://')) {
        return null;
    }

    try {
        return new URL(candidate).toString();
    } catch {
        return null;
    }
}

const PROVIDERS_WITH_AUTOPLAY_PARAM = ['youtube.com', 'youtube-nocookie.com', 'player.vimeo.com', 'dailymotion.com'];

// The SoundCloud widget spells it differently. Spotify has none: its embed
// refuses to start on its own for a listener who is not signed in, so asking
// would only add a parameter that does nothing.
const AUTOPLAY_PARAM_BY_PROVIDER = { 'w.soundcloud.com': 'auto_play' };

/**
 * The player URL asked to start playing on load: the reader has just clicked
 * to get the player, so a paused one would cost a second click for nothing.
 * Only providers whose parameter is known get it; the rest load as they are.
 *
 * @param {string} embedSrc
 * @returns {string}
 */
export function autoplayingSrc(embedSrc) {
    let parsed;
    try {
        parsed = new URL(embedSrc);
    } catch {
        return embedSrc;
    }

    const host = parsed.hostname.toLowerCase().replace(/^www\./, '');
    const ownParam = AUTOPLAY_PARAM_BY_PROVIDER[host];

    if (ownParam) {
        parsed.searchParams.set(ownParam, 'true');

        return parsed.toString();
    }

    if (!PROVIDERS_WITH_AUTOPLAY_PARAM.includes(host)) {
        return embedSrc;
    }

    parsed.searchParams.set('autoplay', '1');

    return parsed.toString();
}
